import React, { useRef, useState } from 'react';
import imageUrlBuilder from '@sanity/image-url';

import { Box } from '@tmap/mmm-style-guide/src/Box';
import { Button } from '@tmap/mmm-style-guide/src/Button';
import { Card } from '@tmap/mmm-style-guide/src/Card';
import { Chip } from '@tmap/mmm-style-guide/src/Chip';
import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import {
  ArrowForward,
  ExpandMore,
  ExpandLess,
} from '@tmap/mmm-style-guide/src/Icon';
import { Collapse } from '@tmap/mmm-style-guide/src/Transition';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';
import { styled } from '@tmap/mmm-style-guide/src/styled';
import stopEvent from '@tmap/mmm-style-guide/src/util/stopEvent';

import client from '../../client';
import LinkCard from '../linkCard';
import { getTagPills } from '../../lib/tags';
import BlockContent from '../block-content';
import { ImagePreview } from './shared/imagePreview';
import { VideoPreview } from './shared/videoPreview';
import CommunityCounts from './shared/counts';
import useQuickApply from './shared/useQuickApply';
import getCommunityStats from './shared/getCommunityStats';
import getCommonProps from './shared/getCommonProps';
import CommunityImpressionWrapper from './shared/impressionWrapper';

const builder = imageUrlBuilder(client);

const imgStyles = () => ({
  width: '100%',
  objectFit: 'cover',
  borderRadius: '8px',
});
const StyledPicture = styled('picture')(imgStyles);
const StyledImg = styled('img')(imgStyles);

const ViewMoreBox = styled(Box)(({ theme }) => ({
  height: '100%',
  maxHeight: '84px',
  width: '100%',
  textAlign: 'center',
  backgroundColor: theme.palette.grey[700],
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.common.white,
}));

const TestimonialCard = styled(Card)(({ theme }) => ({
  width: '100%',
  backgroundColor: theme.palette.primary.veryLight,
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  '.MuiTypography-gutterBottom': {
    fontSize: '12px',
    fontStyle: 'italic',
    textAlign: 'center',
    color: theme.palette.primary.dark,
  },
}));

function HorizontalCommunityCard(props) {
  const {
    community,
    shouldLazyLoad,
    lazyProps,
    placementId,
    pageId,
    hideApplyLink = false,
    populationFormat = 'standard',
  } = props;

  const {
    communityUrl,
    altText,
    primaryImage,
  } = getCommonProps(community);

  const { handleQuickApply, hasQuickApply } = useQuickApply(community);
  const showApplyLink = !hideApplyLink && hasQuickApply;

  const communityStats = getCommunityStats(community, populationFormat);

  const favoriteTarget = useRef();
  const exploreTarget = useRef();

  const [showMore, setShowMore] = useState(false);
  const toggleShowMore = (e) => {
    stopEvent(e);
    setShowMore(!showMore);
  };

  const primaryImageWidth = 328;
  const primaryImageHeight = 266;

  const galleryImagesGrid = community?.galleryImages?.filter((img) => ['standardImage', 'videoBlock'].includes(img._type)).slice(0, 5);
  const numberOfAdditionalImages = (community?.galleryImages || []).length - 5;

  const latestTestimonial = community?.testimonials?.[(community?.testimonials || []).length - 1];

  const truncatedText = community?.longDescription?.slice(0, latestTestimonial ? 375 : 550);
  const hasMoreText = (community?.longDescription || '').length - (truncatedText || '').length > 0;

  const textToDisplay = hasMoreText ? truncatedText.replace(/[^a-zA-Z0-9]*$/g, '') : community?.longDescription;

  return (
    <CommunityImpressionWrapper
      community={community}
      placementId={placementId}
      pageId={pageId}
      favoriteTarget={favoriteTarget}
      exploreTarget={exploreTarget}
    >
      <LinkCard href={communityUrl} alt={altText}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <LinkCard.ImageWrapper>
              <LinkCard.Overlays sx={{ padding: 1 }}>
                <LinkCard.PillBar>
                  {community.tags && getTagPills(community.tags)
                    .map(({ label, icon, iconColor }, i) => (
                      <LinkCard.Pill key={`comm-${community._id}-pill-${i}`} label={label} icon={icon} iconColor={iconColor} />
                    ))}
                </LinkCard.PillBar>
                <LinkCard.FavoriteButton ref={favoriteTarget} entity={community} twoToneHeart />
              </LinkCard.Overlays>
              <LinkCard.Image borderRadius='8px' dimTop shouldLazyLoad={shouldLazyLoad} lazyProps={lazyProps}>
                <StyledPicture>
                  <source
                    srcSet={builder.image(primaryImage).format('webp').width(primaryImageWidth).height(primaryImageHeight)
                      .url()}
                    width={primaryImageWidth}
                    height={primaryImageHeight}
                  />
                  <StyledImg
                    src={builder.image(primaryImage).format('webp').width(primaryImageWidth).height(primaryImageHeight)
                      .url()}
                    width={primaryImageWidth}
                    height={primaryImageHeight}
                    alt={altText}
                  />
                </StyledPicture>
              </LinkCard.Image>
            </LinkCard.ImageWrapper>
          </Grid>
          <Grid item container xs={12} md={5} direction='column'>
            <Grid item xs container alignContent='flex-start'>
              <Grid item xs sx={{ mb: 3 }}>
                <Typography variant='h6' fontWeight={600}>
                  {community.locationText}
                </Typography>
              </Grid>
              {community.regionText && (
                <Grid item sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                  <Chip
                    variant='pill'
                    size='small'
                    label={community.regionText}
                  />
                </Grid>
              )}
              {communityStats.map((info, i) => (
                <Grid item container sx={{ borderTop: (theme) => (i > 0 ? `1px solid ${theme.palette.grey[300]}` : 0), py: 1 }} key={`${community._id}-${info.title}`}>
                  <Grid item xs>
                    <Typography variant='body2'>{info.title}</Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant='body2' color='textSecondary'>
                      {info.content}
                    </Typography>
                  </Grid>
                </Grid>
              ))}
            </Grid>
            <Grid item>
              <Button variant='text' onClick={toggleShowMore} sx={{ ml: -0.5 }} color='inverted'>
                {showMore ? (<>Hide More Detail <ExpandLess /></>)
                  : (<>Show More Detail <ExpandMore /></>)}
              </Button>
            </Grid>
          </Grid>
          <Grid item container xs={12} md={3} direction='column' sx={{ paddingBottom: 0.5 }}>
            <Grid xs item container direction='column' spacing={1.5} paddingTop={0.5}>
              <CommunityCounts
                community={community}
                showApplyLink={showApplyLink}
                handleQuickApply={handleQuickApply}
                variant='stacked'
              />
            </Grid>
            <Grid item>
              <Button ref={exploreTarget} variant='contained' color='inverted' fullWidth disableElevation>
                Explore the Area
              </Button>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Collapse in={showMore}>
              <Grid container spacing={4}>
                <Grid item xs={12} md={4}>
                  <Typography variant='body1' sx={{ mb: 1.5 }}>Photos</Typography>
                  <Grid item container spacing={1} xs={12}>
                    {galleryImagesGrid.map((media, i) => (
                      <Grid item xs={4} key={`${community._id}-img-${i}`}>
                        {
                          media._type === 'standardImage' && <ImagePreview image={media} altText={altText} />
                        }
                        {
                          media._type === 'videoBlock' && <VideoPreview media={media} />
                        }
                      </Grid>
                    ))}
                    {numberOfAdditionalImages > 0 && (
                      <Grid item xs={4} key={`${community._id}-imgs-view-more`}>
                        <ViewMoreBox>
                          <Typography sx={{ fontWeight: 500 }}>
                            View {numberOfAdditionalImages}+ More
                          </Typography>
                        </ViewMoreBox>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
                <Grid item container xs={12} md={latestTestimonial ? 5 : 8}>
                  {textToDisplay && (
                    <Grid item container direction='column'>
                      <Grid item xs>
                        <Typography variant='body1' sx={{ mb: 1.5 }}>About {community.locationText}</Typography>
                        <Typography variant='body2'>{textToDisplay}{hasMoreText && '...'}</Typography>
                      </Grid>
                      <Grid item>
                        {hasMoreText && (
                          <Button variant='text' color='inverted' sx={{ ml: -0.5 }}>
                            Read More <ArrowForward color='primary' fontSize='small' sx={{ ml: 0.5 }} />
                          </Button>
                        )}
                      </Grid>
                    </Grid>
                  )}
                </Grid>
                {latestTestimonial && (
                  <Grid item container xs={12} md={3}>
                    <TestimonialCard elevation={0}>
                      <BlockContent value={latestTestimonial?.body} nested />
                      <Typography fontSize='12px' sx={{ mt: 2, textAlign: 'center' }}>
                        - {latestTestimonial.profile?.name}, {latestTestimonial.profile?.title}
                      </Typography>
                    </TestimonialCard>
                  </Grid>
                )}
              </Grid>
            </Collapse>
          </Grid>
        </Grid>
      </LinkCard>
    </CommunityImpressionWrapper>
  );
}

export default HorizontalCommunityCard;
